// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * Acquisition-funnel analytics (G04, 2026-09-25; Meta Pixel 2026-10-16). Two
 * independent sinks, each behind ITS OWN consent purpose (lib/consentGate.ts)
 * and each lazy-loaded — neither script is ever fetched for a visitor who
 * hasn't opted in to that purpose:
 *   - PostHog (consent key bb_marketing_consent, "Produktanalyse"): our own
 *     funnel dashboards (VITE_POSTHOG_KEY/HOST, unset in dev — every event
 *     still logs to the console). Identified by our own account id only
 *     (identifyUser) — never the email.
 *   - Meta Pixel (consent key bb_ads_consent, "Marketing"): ad measurement for
 *     paid Facebook/Instagram campaigns (VITE_META_PIXEL_ID). PageView on every
 *     route, Lead on sign-up, a custom AppStoreClick on the App Store link.
 *     The Whop Pixel this replaced is gone with Whop (ADR-085).
 * Meta eventID == PostHog event uuid (dedupe key for CAPI later): for the
 * events Meta also receives (signup_completed -> Lead, app_store_clicked ->
 * AppStoreClick) the uuid that posthog-js `capture()` returns is passed to
 * Meta as `eventID`, so a later server-side Conversions API send
 * (docs/work-in-progress/TRACKING-WEEK2.md) can dedupe against both. Only when
 * PostHog did not capture the event (its consent is denied/unset, or no key)
 * does Meta get a freshly generated id and PostHog gets nothing extra.
 * An event may go to one sink and not the other; the gates never mix.
 * Purchase/trial/subscription truth is never read from either of these —
 * that stays server-side (Apple App Store Server API / signed webhooks). This
 * module only ever records that a step happened; it never grants access.
 */
import {
  getAdsConsent,
  getMarketingConsent,
  onAdsConsentChange,
  onMarketingConsentChange,
  type MarketingConsent,
} from "./consentGate"

/**
 * The primary acquisition funnel, in order. ADR-083 (2026-09-25) is the
 * current model — a 14-day, no-card free period, activated AUTOMATICALLY
 * right after onboarding, is the official BiteBalance business model
 * (superseding the "most sessions never touch Whop" reading this comment
 * carried until the same day):
 *
 *   meal_plan_generated → trial_started → whop_flow_completed →
 *   trial_activated → plan_ready_viewed → (…days later…) paywall_viewed →
 *   checkout_started → whop_flow_completed (again) → subscription_purchased
 *
 * `trial_started` fires when the automatic (card-free) Whop checkout is
 * triggered — onboarding-finish navigates straight to /app/access with
 * `autoActivateFreeClaim`, which runs the SAME checkout a manual "Claim
 * your 2 weeks free" click uses (AccessPage.tsx). `whop_flow_completed`
 * fires on EVERY return from a Whop checkout, free or paid — check its
 * `planKey` prop to tell them apart. `trial_activated` fires once the
 * signed webhook has actually granted the entitlement (never assumed from
 * the redirect). `paywall_viewed` now means something different than it
 * did before this ADR: the REAL choose-a-plan surface, reached only when
 * the free period is ending (or already over) — it deliberately does not
 * fire during automatic activation, which shows no offer UI at all.
 * `app_continued_free` is now a legacy-cohort signal only (a grandfathered
 * pre-2026-09-12 `pilot` holder continuing) — expected to be rare and
 * shrinking, not a primary branch.
 */
export type FunnelEventName =
  | "landing_page_viewed"
  | "signup_started"
  | "signup_completed"
  | "onboarding_started"
  | "onboarding_completed"
  | "meal_plan_generated"
  | "trial_started"
  | "trial_activated"
  | "plan_ready_viewed"
  | "paywall_viewed"
  | "checkout_started"
  | "whop_flow_completed"
  | "app_continued_free"
  | "kitchen_opened"
  | "amy_opened"
  | "app_store_clicked"

type Props = Record<string, string | number | boolean | null | undefined>
type PosthogClient = typeof import("posthog-js").default

const META_PIXEL_ID = import.meta.env.VITE_META_PIXEL_ID as string | undefined
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined
const POSTHOG_HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) || "https://eu.i.posthog.com"

/** How long Meta waits for PostHog's capture result before it falls back to its own id. */
const POSTHOG_UUID_WAIT_MS = 3_000

/** Per-event hand-over of PostHog's event uuid to the Meta delivery of the SAME event. */
interface IdTicket {
  uuid: Promise<string | undefined>
  /** Idempotent: the first call wins. `undefined` = PostHog did not (and will not) capture this event. */
  settle: (uuid: string | undefined) => void
}

function createTicket(): IdTicket {
  let settle: IdTicket["settle"] = () => undefined
  const uuid = new Promise<string | undefined>(resolve => {
    settle = resolve
  })
  return { uuid, settle }
}

type PendingEvent = { name: FunnelEventName; props?: Props; ticket: IdTicket }

let posthogReady: Promise<PosthogClient> | null = null
let metaPixelLoaded = false
// Held in memory only, per sink, while THAT sink's consent is still undecided.
let pendingPostHog: PendingEvent[] = []
let pendingMeta: PendingEvent[] = []
// Our own account id, once signed in. Applied to PostHog only while (and as soon as) it is consented + loaded.
let identifiedUserId: string | null = null
// The page the visitor is on, and the last one handed to Meta: a later grant sends the CURRENT page only.
let currentPath: string | null = null
let metaPageViewSentFor: string | null = null

type MetaFbq = (...args: unknown[]) => void

declare global {
  interface Window {
    fbq?: MetaFbq
    _fbq?: MetaFbq
  }
}

/** A fresh event id: Meta's `eventID` when PostHog captured nothing for the event. */
export function sharedEventId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID()
  } catch {
    // fall through to the non-crypto id
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

function applyIdentity(posthog: PosthogClient) {
  if (identifiedUserId && getMarketingConsent() === "granted") posthog.identify(identifiedUserId)
}

function loadPostHog() {
  if (!POSTHOG_KEY) return null
  if (!posthogReady) {
    posthogReady = import("posthog-js").then(mod => {
      const posthog = mod.default
      posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        // Consent is already granted before this ever loads (see gating
        // below) — no extra in-SDK consent wall needed. Our own
        // landing_page_viewed event replaces PostHog's autocapture/pageview.
        autocapture: false,
        capture_pageview: false,
        persistence: "localStorage",
      })
      // A visitor who withdrew earlier was opted out (see syncPostHogOptState); PostHog remembers that across
      // loads. Granted again now -> capturing must be back on, or "On" in the switch would silently capture nothing.
      if (getMarketingConsent() === "granted" && posthog.has_opted_out_capturing()) posthog.opt_in_capturing()
      applyIdentity(posthog)
      return posthog
    })
  }
  return posthogReady
}

/**
 * Meta's standard pixel base code (the `fbq` stub that queues calls until fbevents.js arrives), written out
 * here instead of pasted into index.html so that NOTHING is fetched before the Marketing consent. Called only
 * from paths that have just checked `getAdsConsent() === "granted"`. Returns whether `fbq` is usable.
 */
function loadMetaPixel(): boolean {
  if (metaPixelLoaded) return true
  if (!META_PIXEL_ID || typeof window === "undefined" || typeof document === "undefined") return false
  if (getAdsConsent() !== "granted") return false
  metaPixelLoaded = true
  if (!window.fbq) {
    const queue: unknown[][] = []
    const fbq = function (...args: unknown[]) {
      const self = fbq as unknown as { callMethod?: (...a: unknown[]) => void }
      if (self.callMethod) self.callMethod(...args)
      else queue.push(args)
    } as MetaFbq & { push: unknown; loaded: boolean; version: string; queue: unknown[][] }
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = "2.0"
    fbq.queue = queue
    window.fbq = fbq
    if (!window._fbq) window._fbq = fbq
    const script = document.createElement("script")
    script.async = true
    script.src = "https://connect.facebook.net/en_US/fbevents.js"
    const first = document.getElementsByTagName("script")[0]
    if (first?.parentNode) first.parentNode.insertBefore(script, first)
    else document.head.appendChild(script)
  }
  // Only the events this module sends: no automatic button-click / form-field / microdata collection.
  window.fbq?.("set", "autoConfig", false, META_PIXEL_ID)
  window.fbq?.("init", META_PIXEL_ID)
  return true
}

/**
 * Withdrawal is as easy as giving consent and takes effect IMMEDIATELY (GDPR Art. 7(3)): on anything but
 * "granted" PostHog — if it is already loaded in this session — is opted out of capturing, and "granted"
 * again opts it back in (and re-applies the identity). PostHog that was never loaded stays unloaded.
 */
function syncPostHogOptState(consent: MarketingConsent) {
  if (consent === "granted") {
    void loadPostHog()?.then(posthog => {
      if (posthog.has_opted_out_capturing()) posthog.opt_in_capturing()
      applyIdentity(posthog)
    })
    return
  }
  void posthogReady?.then(posthog => posthog.opt_out_capturing())
}

/**
 * Same for the Meta Pixel, via its own consent API: once loaded, a non-granted state revokes (the pixel then
 * sends nothing, and `deliverMeta` below stops calling it too); granted again grants. A pixel that was never
 * loaded stays unloaded.
 */
function syncMetaConsent(consent: MarketingConsent) {
  if (!metaPixelLoaded) return
  window.fbq?.("consent", consent === "granted" ? "grant" : "revoke")
}

function metaCall(name: FunnelEventName, props: Props | undefined, eventID: string): unknown[] | null {
  if (name === "signup_completed") return ["track", "Lead", {}, { eventID }]
  if (name === "app_store_clicked") {
    return ["trackCustom", "AppStoreClick", { placement: props?.placement }, { eventID }]
  }
  return null
}

/** Whether Meta receives this event at all (page views have their own path). */
function reachesMeta(name: FunnelEventName): boolean {
  return metaCall(name, undefined, "") !== null
}

function deliverPostHog(name: FunnelEventName, props: Props | undefined, ticket: IdTicket) {
  // Last line of defence behind trackFunnelEvent's own check: nothing reaches PostHog unless the CURRENT
  // recorded Produktanalyse choice is "granted".
  if (getMarketingConsent() !== "granted") {
    ticket.settle(undefined)
    return
  }
  const ready = loadPostHog()
  if (!ready) {
    ticket.settle(undefined)
  } else {
    void ready
      .then(posthog => {
        ticket.settle(posthog.capture(name, props)?.uuid)
      })
      .catch(() => ticket.settle(undefined))
  }
  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.log(`[funnel event] ${name}`, props ?? {})
  }
}

async function deliverMeta(name: FunnelEventName, props: Props | undefined, ticket: IdTicket) {
  // Same last line of defence, on the Marketing choice.
  if (getAdsConsent() !== "granted" || !reachesMeta(name)) return
  // Same event, same id: if PostHog is capturing this event, wait for the uuid it assigned.
  let eventID: string | undefined
  if (getMarketingConsent() === "granted") {
    eventID = await Promise.race([
      ticket.uuid,
      new Promise<undefined>(resolve => setTimeout(() => resolve(undefined), POSTHOG_UUID_WAIT_MS)),
    ])
  }
  // Withdrawn while waiting -> nothing is sent.
  if (getAdsConsent() !== "granted") return
  const call = metaCall(name, props, eventID ?? sharedEventId())
  if (call && loadMetaPixel()) window.fbq?.(...call)
}

function sendMetaPageView() {
  if (getAdsConsent() !== "granted" || currentPath === null) return
  if (metaPageViewSentFor === currentPath) return
  if (!loadMetaPixel()) return
  metaPageViewSentFor = currentPath
  window.fbq?.("track", "PageView")
}

/** Call once, at app root — wires the consent listeners. Loading the SDKs themselves is deferred until the
 * matching consent is actually granted. Returns a disposer for the listeners. */
export function initAnalytics(): () => void {
  if (getMarketingConsent() === "granted") loadPostHog()
  if (getAdsConsent() === "granted") loadMetaPixel()
  const stopMarketing = onMarketingConsentChange(consent => {
    syncPostHogOptState(consent)
    if (consent === "granted") {
      loadPostHog()
      const queued = pendingPostHog
      pendingPostHog = []
      for (const { name, props, ticket } of queued) deliverPostHog(name, props, ticket)
    } else {
      // Declined (or the choice was reset to "unset" by the footer's Cookie settings) — drop anything
      // queued, never deliver it.
      for (const { ticket } of pendingPostHog) ticket.settle(undefined)
      pendingPostHog = []
    }
  })
  const stopAds = onAdsConsentChange(consent => {
    syncMetaConsent(consent)
    if (consent === "granted") {
      loadMetaPixel()
      const queued = pendingMeta
      pendingMeta = []
      for (const { name, props, ticket } of queued) void deliverMeta(name, props, ticket)
      sendMetaPageView() // the CURRENT page only — earlier page views are never replayed
    } else {
      pendingMeta = []
    }
  })
  return () => {
    stopMarketing()
    stopAds()
  }
}

/**
 * Records a funnel step. Before a purpose's consent is decided, the event is held in memory only (never sent
 * to a third party, never written to disk) so an early step like landing_page_viewed isn't lost if the visitor
 * accepts a few seconds later; a "denied" verdict never delivers it. Each sink decides on its own gate.
 */
export function trackFunnelEvent(name: FunnelEventName, props?: Props): void {
  const ticket = createTicket()

  const analytics = getMarketingConsent()
  if (analytics === "granted") deliverPostHog(name, props, ticket)
  else if (analytics === "unset") pendingPostHog.push({ name, props, ticket })
  else ticket.settle(undefined)

  if (reachesMeta(name)) {
    const ads = getAdsConsent()
    if (ads === "granted") void deliverMeta(name, props, ticket)
    else if (ads === "unset") pendingMeta.push({ name, props, ticket })
  }

  if (import.meta.env.DEV && analytics !== "granted") {
    // eslint-disable-next-line no-console
    console.log(`[funnel event, consent=${analytics}] ${name}`, props ?? {})
  }
}

/**
 * Meta PageView for the page the visitor is on (components/RouteTracker.tsx, on first load and every route
 * change). Dropped while the Marketing consent is unset/denied — but the path is remembered, so a LATER grant
 * sends the current page and nothing older. PostHog pageviews stay off (landing_page_viewed is its entry event).
 * /auth/callback is skipped: its URL carries the single-use OAuth `code` and `state`, which must not reach a
 * third party.
 */
export function trackPageView(pathname: string): void {
  if (pathname.startsWith("/auth/callback")) return
  currentPath = pathname
  sendMetaPageView()
}

/**
 * Ties PostHog to our own account id (the /v1/me `id`, never the email). Remembered in module state, so a
 * Produktanalyse consent granted LATER in the session identifies then.
 */
export function identifyUser(userId: string): void {
  identifiedUserId = userId
  if (getMarketingConsent() !== "granted") return
  void loadPostHog()?.then(posthog => applyIdentity(posthog))
}

/** Sign-out: forget the id and give PostHog a fresh anonymous identity (a shared device must not link users). */
export function resetUser(): void {
  identifiedUserId = null
  void posthogReady?.then(posthog => posthog.reset())
}
